import {
  ConnectedPosition,
  Overlay,
  OverlayPositionBuilder,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import {
  Component,
  ComponentRef,
  ContentChild,
  Directive,
  ElementRef,
  HostListener,
  Injector,
  Input,
  OnInit,
  TemplateRef,
  inject, OnDestroy,
} from '@angular/core';

enum TooltipPosition {
  ABOVE = 'above',
  BELOW = 'below',
  LEFT = 'left',
  RIGHT = 'right',
  DEFAULT = 'above',
}

@Directive({
  standalone: true,
  selector: '[tooltip]',
})
export class TooltipDirective implements OnInit, OnDestroy {
  @Input('tooltip') tooltipText = '';
  @Input() tooltipTemplate!: TemplateRef<any>;
  @Input() tooltipPosition: string = TooltipPosition.DEFAULT;

  private overlayRef!: OverlayRef;
  private overlay = inject(Overlay);
  private overlayPostionBuilder: OverlayPositionBuilder = inject(
    OverlayPositionBuilder
  );
  private elementRef: ElementRef = inject(ElementRef);
  private injector: Injector = inject(Injector);

  ngOnInit(): void {
    const positionStrategy = this.overlayPostionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions(
        this.determinePositionStrategy(
          this.determineValidPosition(this.tooltipPosition)
            ? this.tooltipPosition
            : TooltipPosition.DEFAULT
        )
      );
    this.overlayRef = this.overlay.create({ positionStrategy });
  }

  @HostListener('mouseenter')
  showTooltip(): void {
    const tooltipPortal = new ComponentPortal(TooltipComponent);

    const tooltipRef: ComponentRef<TooltipComponent> =
      this.overlayRef.attach(tooltipPortal);

    tooltipRef.instance.content = this.tooltipTemplate;
    tooltipRef.instance.tooltipText = this.tooltipText;
    tooltipRef.instance.position = this.tooltipPosition;
  }

  @HostListener('mouseleave')
  hideTooltip(): void {
    this.overlayRef.detach();
  }

  ngOnDestroy() {
    this.hideTooltip()
  }

  private determineValidPosition(
    position: string
  ): position is TooltipPosition {
    return Object.values<string>(TooltipPosition).includes(position);
  }

  private determinePositionStrategy(
    position: TooltipPosition
  ): ConnectedPosition[] {
    switch (position) {
      case TooltipPosition.ABOVE:
        return [
          {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
          },
        ];
      case TooltipPosition.BELOW:
        return [
          {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
          },
        ];
      case TooltipPosition.LEFT:
        return [
          {
            originX: 'start',
            originY: 'center',
            overlayX: 'end',
            overlayY: 'center',
          },
        ];
      case TooltipPosition.RIGHT:
        return [
          {
            originX: 'end',
            originY: 'center',
            overlayX: 'start',
            overlayY: 'center',
          },
        ];
    }
  }
}

@Component({
    selector: 'app-tooltip',
    templateUrl: './tooltip.component.html',
    styleUrls: ['./tooltip.component.scss'],
    imports: [CommonModule]
})
export class TooltipComponent {
  @Input() tooltipText = '';
  @Input() content!: TemplateRef<any>;
  @Input() position: string = TooltipPosition.DEFAULT;
}
